import type {
  BuildMultiAction,
  BuyTechCardAction,
  BuyTechCoinAction,
  EndPhaseAction,
  MoveUnitAction,
  ProduceUnitAction,
  RevealTechCardAction,
  SurrenderAction,
  TechCardId,
  TriAngerAction,
  UnitTypeId,
} from "@cloudnest/tri-anger-engine";

export type TriAngerReplayEventType = "init" | TriAngerAction["type"];

export const TRI_ANGER_REPLAY_EVENT_TYPES = [
  "init",
  "MoveUnit",
  "ProduceUnit",
  "BuildMulti",
  "BuyTechCoin",
  "BuyTechCard",
  "RevealTechCard",
  "EndPhase",
  "Surrender",
] as const satisfies readonly TriAngerReplayEventType[];

export function normalizeTriAngerReplayEventType(
  eventType: string,
): TriAngerReplayEventType {
  if ((TRI_ANGER_REPLAY_EVENT_TYPES as readonly string[]).includes(eventType)) {
    return eventType as TriAngerReplayEventType;
  }
  throw new Error(`Unsupported Tri-Anger event type: ${eventType}`);
}

export function triAngerEventToAction(
  eventType: string,
  payload: Record<string, unknown>,
): TriAngerAction {
  const type = normalizeTriAngerReplayEventType(eventType);

  switch (type) {
    case "init":
      throw new Error("triAngerEventToAction cannot convert the init event");
    case "MoveUnit":
      return {
        type,
        playerId: payload.playerId as string,
        unitId: payload.unitId as string,
        to: payload.to as MoveUnitAction["to"],
      };
    case "ProduceUnit":
      return {
        type,
        playerId: payload.playerId as string,
        unitTypeId: payload.unitTypeId as UnitTypeId,
        workerUnitId: payload.workerUnitId as string | undefined,
      } satisfies ProduceUnitAction;
    case "BuildMulti":
      return {
        type,
        playerId: payload.playerId as string,
        at: payload.at as BuildMultiAction["at"],
      };
    case "BuyTechCoin":
      return {
        type,
        playerId: payload.playerId as string,
      } satisfies BuyTechCoinAction;
    case "BuyTechCard":
      return {
        type,
        playerId: payload.playerId as string,
        techCardId: payload.techCardId as TechCardId,
      } satisfies BuyTechCardAction;
    case "RevealTechCard":
      return {
        type,
        playerId: payload.playerId as string,
        techCardId: payload.techCardId as TechCardId,
        target: payload.target as RevealTechCardAction["target"],
      };
    case "EndPhase":
      return {
        type,
        playerId: payload.playerId as string,
      } satisfies EndPhaseAction;
    case "Surrender":
      return {
        type,
        playerId: payload.playerId as string,
      } satisfies SurrenderAction;
  }
}
